/**
 * InactivityProvider — Detecta inatividade e exibe LockScreen.
 * Envolve rotas autenticadas. Apenas desktop (não roda no mobile/PWA).
 *
 * Persiste estado de bloqueio no sessionStorage para sobreviver a F5.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { LockScreen } from './LockScreen';
import { useAuthStore } from '../../store/authStore';

import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import { getApprovalPreferences } from '../../security/deviceApprovalApi';
const EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
const CHECK_INTERVAL = 15_000; // Verificar a cada 15s
const LOCK_KEY = 'ac_lock_state'; // sessionStorage key
const UNLOCK_CHANNEL = 'ac_lock_broadcast';

/** Rotas públicas onde o LockScreen NUNCA deve aparecer */
const PUBLIC_ROUTES = ['/login', '/convite', '/ativar', '/conectando', '/404'];

interface Props {
  children: React.ReactNode;
}

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
}

/** Restaura estado de bloqueio do sessionStorage (sobrevive a F5) */
function restoreLockState(): { locked: boolean; lockedAt: Date | null } {
  if (import.meta.env.DEV) {
    sessionStorage.removeItem(LOCK_KEY);
    return { locked: false, lockedAt: null };
  }
  try {
    const raw = sessionStorage.getItem(LOCK_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      return { locked: true, lockedAt: new Date(data.lockedAt) };
    }
  } catch { /* ignorar parse errors */ }
  return { locked: false, lockedAt: null };
}

function saveLockState(lockedAt: Date) {
  if (import.meta.env.DEV) return;
  sessionStorage.setItem(LOCK_KEY, JSON.stringify({ lockedAt: lockedAt.toISOString() }));
}

function clearLockState() {
  sessionStorage.removeItem(LOCK_KEY);
}

export function InactivityProvider({ children }: Props) {
  const restored = restoreLockState();
  const [locked, setLocked] = useState(restored.locked);
  const [lockedAt, setLockedAt] = useState<Date | null>(restored.lockedAt);
  const [prefs, setPrefs] = useState<any>(null);
  const lastActivity = useRef(Date.now());
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const location = useLocation();

  /** Em rotas públicas, nunca exibir lock (mesmo que usuário esteja autenticado) */
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route));

  // Limpar lock quando usuario faz logout (user → null)
  useEffect(() => {
    if (!user) {
      clearLockState();
      setLocked(false);
      setLockedAt(null);
    }
  }, [user]);

  // Ouvir desbloqueio de outras abas via BroadcastChannel
  useEffect(() => {
    const bc = new BroadcastChannel(UNLOCK_CHANNEL);
    bc.onmessage = (ev) => {
      if (ev.data?.type === 'unlock') {
        clearLockState();
        setLocked(false);
        setLockedAt(null);
        lastActivity.current = Date.now();
      }
    };
    return () => bc.close();
  }, []);

  // Ouvir evento manual de bloqueio (ex: botão no header)
  useEffect(() => {
    const handleForceLock = () => {
      const now = new Date();
      setLocked(true);
      setLockedAt(now);
      saveLockState(now);
    };
    window.addEventListener('ac_force_lock', handleForceLock);
    return () => window.removeEventListener('ac_force_lock', handleForceLock);
  }, []);

  // Buscar preferências de segurança
  useEffect(() => {
    if (!user || isMobileDevice()) return;
    const token = localStorage.getItem('ac_token') || '';
    getApprovalPreferences({ bearerToken: token })
      .then(data => { if (data) setPrefs(data); })
      .catch(() => {});
  }, [user]);

  // Listener de atividade
  const resetActivity = useCallback(() => {
    lastActivity.current = Date.now();
    if (locked) return; // Não desbloquear automaticamente
  }, [locked]);

  useEffect(() => {
    if (isMobileDevice() || !user) return;
    EVENTS.forEach(e => document.addEventListener(e, resetActivity, { passive: true }));
    return () => { EVENTS.forEach(e => document.removeEventListener(e, resetActivity)); };
  }, [resetActivity, user]);

  // Timer de verificação
  useEffect(() => {
    if (isMobileDevice() || !user || !prefs || import.meta.env.DEV) return;

    const userTimeout = Number(localStorage.getItem('ac_session_timeout'));
    const timeoutMs = (userTimeout || prefs?.timeoutBloqueioMinutos || 60) * 60 * 1000;
    const disconnectMs = (prefs.timeoutDesconectarMinutos || 30) * 60 * 1000;

    const iv = setInterval(() => {
      if (locked) {
        // Verificar auto-desconectar
        if (lockedAt) {
          const elapsed = Date.now() - lockedAt.getTime();
          if (elapsed >= disconnectMs) {
            clearLockState();
            logout();
          }
        }
        return;
      }

      const idle = Date.now() - lastActivity.current;
      if (idle >= timeoutMs) {
        const now = new Date();
        setLocked(true);
        setLockedAt(now);
        saveLockState(now);
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(iv);
  }, [user, prefs, locked, lockedAt, logout]);

  // Rota pública, mobile, sem user ou em ambiente de desenvolvimento → sem lock
  if (isPublicRoute || isMobileDevice() || !user || import.meta.env.DEV) return <>{children}</>;

  // Lock screen ativo
  if (locked && lockedAt && prefs) {
    const passKeyMinutes = prefs.timeoutPasskeyMinutos || 30;
    const passKeyUntil = new Date(lockedAt.getTime() + passKeyMinutes * 60 * 1000);

    // Detectar se este dispositivo (desktop) tem Passkey cadastrada
    const passKeyDevices: string[] = (user as any).passKeyDevices || [];
    const hasDesktopPasskey = passKeyDevices.includes('desktop');

    // Detectar se usuario tem senha (senhaHash != null) ou e conta Google
    // O backend retorna hasSenha no JWT validate
    const hasSenha = (user as any).hasSenha !== false; // default true se nao informado
    const isGoogleAccount = !hasSenha;

    // SECURITY: Quando bloqueado, NÃO renderizar children (dashboard, dados, APIs).
    // Isso impede que qualquer dado seja carregado, trafegado ou exposto no DevTools.
    // Antes: children era renderizado com LockScreen overlay por cima (dados expostos).
    // Agora: SOMENTE o LockScreen é renderizado — zero data leaks.
    return (
      <LockScreen
        userName={user.nome || user.email || 'Usuário'}
        userPhoto={user.fotoUrl || null}
        lockedAt={lockedAt}
        passKeyAvailableUntil={passKeyUntil}
        fingerprint={getDeviceFingerprint()}
        hasPasskey={hasDesktopPasskey}
        passKeyDevices={passKeyDevices}
        isGoogleAccount={isGoogleAccount}
        onUnlock={() => {
          setLocked(false);
          setLockedAt(null);
          clearLockState();
          lastActivity.current = Date.now();
        }}
      />
    );
  }

  return <>{children}</>;
}

